import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import vehicleMaintenanceApi from '../../api/vehicleMaintenanceApi';
import { useAuth } from '../../contexts/AuthContext';
import { sortRowsByVehicleCode, sortVehiclesByVehicleCode } from './internalAuditVehicleCodeSort';
import {
  VehicleModuleHero,
  VehiclePageShell,
  VehiclePersonCell,
  VehiclePlateCell,
  vehiclePlateColumnProps,
  VehicleSelectionPreview,
  VehicleStatCard,
  VehicleStatGrid,
  getVehicleById,
} from './vehicleMaintenanceUi';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DataTable from '../../components/ui/DataTable';
import FilterActions from '../../components/ui/FilterActions';
import FilterPanel from '../../components/ui/FilterPanel';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';

const COMPLETION_OPTIONS = [
  { value: 'in_progress', label: 'Devam Ediyor' },
  { value: 'completed', label: 'Tamamlandı' },
];

const initialForm = {
  vehicle: '',
  breakdown_date: '',
  reported_by: '',
  description: '',
  service_status: '',
  completion_status: 'in_progress',
};

const buildBreakdownFormData = (payload, attachmentFile) => {
  const form = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      form.append(key, value);
    }
  });
  if (attachmentFile) {
    form.append('attachment', attachmentFile);
  }
  return form;
};

const CompletionBadge = ({ status, label }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${
      status === 'completed'
        ? 'bg-emerald-100 text-emerald-800 ring-emerald-200/80 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800/50'
        : 'bg-amber-100 text-amber-900 ring-amber-200/80 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800/50'
    }`}
  >
    {label || (status === 'completed' ? 'Tamamlandı' : 'Devam Ediyor')}
  </span>
);

const InternalAuditBreakdownsPage = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [filters, setFilters] = useState({ vehicle_id: '', completion_status: '' });
  const [draftFilters, setDraftFilters] = useState({ vehicle_id: '', completion_status: '' });
  const vehicleDropdownRef = useRef(null);

  const defaultReporter = useMemo(() => {
    if (!user) return '';
    return user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }, [user]);

  const sortedVehicles = useMemo(() => sortVehiclesByVehicleCode(vehicles), [vehicles]);
  const sortedRows = useMemo(() => sortRowsByVehicleCode(rows), [rows]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!vehicleDropdownRef.current) return;
      if (!vehicleDropdownRef.current.contains(event.target)) setShowVehicleDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const [breakdownsResponse, vehiclesResponse] = await Promise.all([
        vehicleMaintenanceApi.getBreakdowns(activeFilters),
        vehicleMaintenanceApi.getVehicles({ is_active: 'true' }),
      ]);
      setRows(breakdownsResponse?.data || []);
      setVehicles(vehiclesResponse?.data || []);
    } catch (err) {
      setError(err.message || 'Arıza / hasar kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSearch = () => {
    setFilters(draftFilters);
    setShowVehicleDropdown(false);
    loadData(draftFilters);
  };

  const handleClear = () => {
    const reset = { vehicle_id: '', completion_status: '' };
    setDraftFilters(reset);
    setFilters(reset);
    setShowVehicleDropdown(false);
    loadData(reset);
  };

  const openCreateModal = () => {
    setEditingRow(null);
    setSelectedRow(null);
    setAttachmentFile(null);
    setFormData({ ...initialForm, reported_by: defaultReporter });
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    if (!row) return;
    setEditingRow(row);
    setAttachmentFile(null);
    setFormData({
      vehicle: row.vehicle || '',
      breakdown_date: row.breakdown_date || '',
      reported_by: row.reported_by || '',
      description: row.description || '',
      service_status: row.service_status || '',
      completion_status: row.completion_status || 'in_progress',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!row) return;
    if (!window.confirm('Arıza / hasar kaydını silmek istediğinize emin misiniz?')) return;
    try {
      await vehicleMaintenanceApi.deleteBreakdown(row.id);
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt silinemedi.');
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        vehicle: formData.vehicle ? Number(formData.vehicle) : null,
        breakdown_date: formData.breakdown_date || null,
        reported_by: formData.reported_by || '',
        description: formData.description || '',
        service_status: formData.service_status || null,
        completion_status: formData.completion_status || 'in_progress',
      };
      const body = attachmentFile ? buildBreakdownFormData(payload, attachmentFile) : payload;

      if (editingRow?.id) {
        await vehicleMaintenanceApi.updateBreakdown(editingRow.id, body);
      } else {
        await vehicleMaintenanceApi.createBreakdown(body);
      }
      setIsModalOpen(false);
      setEditingRow(null);
      setSelectedRow(null);
      setAttachmentFile(null);
      setFormData(initialForm);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      header: 'Araç ID',
      accessorKey: 'vehicle_code',
      cell: ({ getValue }) => (
        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {getValue() || '—'}
        </span>
      ),
    },
    {
      ...vehiclePlateColumnProps,
      cell: ({ row }) => {
        const vehicle = getVehicleById(vehicles, row.original.vehicle);
        return (
          <VehiclePlateCell
            plate={row.original.license_plate}
            brand={vehicle?.brand}
            model={vehicle?.model}
          />
        );
      },
    },
    { header: 'Arıza Tarihi', accessorKey: 'breakdown_date', cell: ({ getValue }) => getValue() || '—' },
    {
      header: 'Bildiren Kullanıcı',
      accessorKey: 'reported_by',
      cell: ({ row }) => <VehiclePersonCell name={row.original.reported_by} subtitle="Bildiren" />,
    },
    {
      header: 'Arıza Açıklaması',
      accessorKey: 'description',
      cell: ({ getValue }) => {
        const value = getValue() || '';
        return value.length > 80 ? `${value.slice(0, 80)}…` : (value || '—');
      },
    },
    { header: 'Servis Durumu', accessorKey: 'service_status', cell: ({ getValue }) => getValue() || '—' },
    {
      header: 'Durum',
      accessorKey: 'completion_status',
      cell: ({ row }) => (
        <CompletionBadge
          status={row.original.completion_status}
          label={row.original.completion_status_display}
        />
      ),
    },
    {
      header: 'Dosya',
      accessorKey: 'attachment_url',
      cell: ({ getValue }) => {
        const url = getValue();
        if (!url) return '—';
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-700 underline hover:text-teal-900 dark:text-teal-400 dark:hover:text-teal-300"
            onClick={(e) => e.stopPropagation()}
          >
            Görüntüle
          </a>
        );
      },
    },
  ], [vehicles]);

  const stats = useMemo(() => {
    const total = sortedRows.length;
    const inProgress = sortedRows.filter((r) => r.completion_status !== 'completed').length;
    const completed = sortedRows.filter((r) => r.completion_status === 'completed').length;
    const withAttachment = sortedRows.filter((r) => r.attachment_url).length;
    return { total, inProgress, completed, withAttachment };
  }, [sortedRows]);

  const selectedVehiclePreview = useMemo(() => {
    if (!selectedRow) return null;
    return getVehicleById(vehicles, selectedRow.vehicle) || {
      license_plate: selectedRow.license_plate,
      vehicle_code: selectedRow.vehicle_code,
      brand: null,
      model: null,
      assigned_person: null,
    };
  }, [selectedRow, vehicles]);

  const getRowClassName = (row) => {
    if (row.completion_status !== 'completed') return 'border-l-[3px] border-l-amber-400 dark:border-l-amber-500';
    return '';
  };

  const renderStatusFilter = () => (
    <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
      <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Durum</legend>
      <select
        className="h-5 w-full bg-transparent text-[10px] text-gray-900 outline-none dark:text-white"
        value={draftFilters.completion_status}
        onChange={(e) => setDraftFilters((prev) => ({ ...prev, completion_status: e.target.value }))}
      >
        <option value="">Tümü</option>
        {COMPLETION_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </fieldset>
  );

  return (
    <VehiclePageShell>
        <VehicleModuleHero
          icon={WrenchScrewdriverIcon}
          title="Arıza / Hasar Takip"
          subtitle="Araç arıza ve hasar kayıtlarını takip edin, servis durumunu ve ekleri yönetin."
          accent="teal"
        />

        <VehicleStatGrid>
          <VehicleStatCard label="Toplam Kayıt" value={stats.total} tone="slate" icon={WrenchScrewdriverIcon} />
          <VehicleStatCard label="Devam Ediyor" value={stats.inProgress} tone="amber" icon={ClockIcon} />
          <VehicleStatCard label="Tamamlandı" value={stats.completed} tone="emerald" icon={CheckCircleIcon} />
          <VehicleStatCard label="Dosyası Var" value={stats.withAttachment} tone="blue" icon={ExclamationTriangleIcon} />
        </VehicleStatGrid>

        {selectedVehiclePreview ? (
          <VehicleSelectionPreview
            vehicle={selectedVehiclePreview}
            personName={selectedRow?.reported_by}
            personLabel="Bildiren"
          />
        ) : null}

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <div className="flex items-center justify-between gap-3">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}

        <FilterPanel compact title="Filtreler" isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((prev) => !prev)}>
          <div className="grid grid-cols-1 gap-1.5">
            <div className="rounded-md border border-teal-100/80 bg-gradient-to-br from-white to-teal-50/40 p-1.5 dark:border-teal-900/40 dark:from-gray-800 dark:to-teal-950/20">
              <div className="sr-only text-teal-800 dark:text-teal-300">
                Filtreler
              </div>
              <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Araç</legend>
                  <div className="relative" ref={vehicleDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowVehicleDropdown((prev) => !prev)}
                      className="flex h-5 w-full items-center justify-between bg-transparent px-0 text-[10px] text-gray-900 outline-none dark:text-white"
                    >
                      <span className="truncate">
                        {(() => {
                          if (!draftFilters.vehicle_id) return 'Tümü';
                          const selected = sortedVehicles.find((v) => String(v.id) === String(draftFilters.vehicle_id));
                          return selected ? `${selected.vehicle_code} - ${selected.license_plate}` : 'Tümü';
                        })()}
                      </span>
                      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                    {showVehicleDropdown ? (
                      <div className="absolute left-0 right-0 z-50 mt-1 max-h-[196px] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                        <button
                          type="button"
                          onClick={() => {
                            setDraftFilters((prev) => ({ ...prev, vehicle_id: '' }));
                            setShowVehicleDropdown(false);
                          }}
                          className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                        >
                          Tümü
                        </button>
                        {sortedVehicles.map((vehicle) => (
                          <button
                            key={vehicle.id}
                            type="button"
                            onClick={() => {
                              setDraftFilters((prev) => ({ ...prev, vehicle_id: String(vehicle.id) }));
                              setShowVehicleDropdown(false);
                            }}
                            className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                          >
                            {vehicle.vehicle_code} - {vehicle.license_plate}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </fieldset>
                {renderStatusFilter()}
              </div>
            </div>
          </div>
          <div className="mt-1.5 flex justify-end border-t border-gray-100 pt-1.5 dark:border-gray-700">
            <FilterActions compact onClear={handleClear} onSearch={handleSearch} />
          </div>
        </FilterPanel>

        <ActionToolbar className="rounded-xl border border-teal-100/80 bg-white/80 px-2 py-1.5 shadow-sm dark:border-teal-900/40 dark:bg-gray-900/60">
          <IconActionButton icon={PlusIcon} onClick={openCreateModal} title="Yeni Kayıt" />
          <IconActionButton icon={PencilSquareIcon} onClick={() => openEditModal(selectedRow)} title="Düzenle" disabled={!selectedRow} />
          <IconActionButton icon={TrashIcon} onClick={() => handleDelete(selectedRow)} title="Sil" disabled={!selectedRow} />
        </ActionToolbar>

        <DataTable
          data={sortedRows}
          columns={columns}
          isLoading={loading}
          showSearch={false}
          onRowClick={setSelectedRow}
          onRowDoubleClick={openEditModal}
          selectedRowId={selectedRow?.id}
          compact
          compactCellWrap
          modern
          getRowClassName={getRowClassName}
        />

      <ResponsiveModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-3xl">
        <div className="px-4 pt-5 pb-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {editingRow ? 'Arıza / Hasar Kaydını Düzenle' : 'Yeni Arıza / Hasar Kaydı'}
            </h2>
            <button type="button" onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSave}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Araç</Label>
                <select
                  required
                  className="flex h-11 md:h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  value={formData.vehicle}
                  onChange={(e) => setFormData((prev) => ({ ...prev, vehicle: e.target.value }))}
                >
                  <option value="">Araç seçin</option>
                  {sortedVehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.vehicle_code} - {vehicle.license_plate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Arıza Tarihi</Label>
                <Input
                  required
                  type="date"
                  value={formData.breakdown_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, breakdown_date: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Bildiren Kullanıcı</Label>
                <Input
                  required
                  value={formData.reported_by}
                  onChange={(e) => setFormData((prev) => ({ ...prev, reported_by: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Servis Durumu</Label>
                <Input
                  value={formData.service_status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, service_status: e.target.value }))}
                  placeholder="Örn: Serviste, parça bekleniyor"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Tamamlandı / Devam Ediyor</Label>
                <select
                  className="flex h-11 md:h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  value={formData.completion_status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, completion_status: e.target.value }))}
                >
                  {COMPLETION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Arıza Açıklaması</Label>
                <textarea
                  required
                  rows={3}
                  className="flex w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Dosya veya Fotoğraf</Label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 cursor-pointer text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <DocumentArrowUpIcon className="h-5 w-5" />
                    <span>{attachmentFile ? attachmentFile.name : 'Dosya seç'}</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  {editingRow?.attachment_url && !attachmentFile ? (
                    <a
                      href={editingRow.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 underline"
                    >
                      Mevcut dosyayı görüntüle
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end space-x-3">
              <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>İptal</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>
    </VehiclePageShell>
  );
};

export default InternalAuditBreakdownsPage;
